import styled from 'styled-components';
import colors from 'styles/colors';

export enum DropPosition {
  Before = 'before',
  After = 'after',
}

export enum DragOrientation {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

// Marks a reorderable chip so findClosestDragTarget can select it without coupling drag behavior to
// test ids. Apply DRAG_TARGET_ATTR to the chip root and query with DRAG_TARGET_SELECTOR.
export const DRAG_TARGET_ATTR = 'data-drag-target';
export const DRAG_TARGET_SELECTOR = `[${DRAG_TARGET_ATTR}]`;

export const DropIndicator = styled.div<{
  $position: DropPosition;
  $orientation?: DragOrientation;
}>`
  --drop-indicator-offset: -5px;
  --drop-indicator-color: ${colors.blue5};
  position: absolute;
  opacity: 0.7;

  ${(props) => {
    const orientation = props.$orientation;
    if (orientation === DragOrientation.Vertical) {
      return `
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: var(--drop-indicator-color);
        ${props.$position === DropPosition.Before ? 'left: var(--drop-indicator-offset);' : 'right: var(--drop-indicator-offset);'}

        &::before,
        &::after {
          content: '';
          position: absolute;
          left: -3px;
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
        }

        &::before {
          top: -2px;
          border-top: 6px solid var(--drop-indicator-color);
        }

        &::after {
          bottom: -2px;
          border-bottom: 6px solid var(--drop-indicator-color);
        }
      `;
    }
    return `
      --horiz-drop-indicator-offset: calc(var(--drop-indicator-offset) - 3px);
      left: 0;
      right: 0;
      height: 2px;
      background-color: var(--drop-indicator-color);
      ${props.$position === DropPosition.Before ? 'top: var(--horiz-drop-indicator-offset);' : 'bottom: var(--horiz-drop-indicator-offset);'}

      &::before,
      &::after {
        content: '';
        position: absolute;
        top: -3px;
        width: 0;
        height: 0;
        border-top: 4px solid transparent;
        border-bottom: 4px solid transparent;
      }

      &::before {
        left: -2px;
        border-left: 6px solid var(--drop-indicator-color);
      }

      &::after {
        right: -2px;
        border-right: 6px solid var(--drop-indicator-color);
      }
    `;
  }}
`;

/** Computes before/after relative to an element's midpoint along the given axis. */
function computeDropPosition(
  orientation: DragOrientation,
  e: React.DragEvent,
  rect: DOMRect
): DropPosition {
  const midpoint =
    orientation === DragOrientation.Horizontal
      ? rect.left + rect.width / 2
      : rect.top + rect.height / 2;
  const cursor = orientation === DragOrientation.Horizontal ? e.clientX : e.clientY;
  return cursor < midpoint ? DropPosition.Before : DropPosition.After;
}

/**
 * Finds the closest chip element to the drag cursor position.
 * Returns the chip index and whether the cursor is before or after the chip's midpoint.
 *
 * "After chip N" is normalized to "Before chip N+1" (when a next chip exists)
 * so the drop indicator always renders on the same edge between two chips,
 * preventing visual jumps when the cursor crosses a chip's midpoint.
 */
export function findClosestDragTarget(
  orientation: DragOrientation = DragOrientation.Vertical,
  dragEvent: React.DragEvent,
  container: Element,
  selector: string
): { index: number; position: DropPosition } | null {
  const chipElements = container.querySelectorAll<HTMLElement>(selector);
  if (chipElements.length === 0) return null;

  let closestIndex = 0;
  let closestPosition = DropPosition.Before as DropPosition;
  let minDistance = Infinity;

  chipElements.forEach((el, i) => {
    const rect = el.getBoundingClientRect();
    const midpoint =
      orientation === DragOrientation.Horizontal
        ? rect.left + rect.width / 2
        : rect.top + rect.height / 2;
    const cursor =
      orientation === DragOrientation.Horizontal ? dragEvent.clientX : dragEvent.clientY;
    const distance = Math.abs(cursor - midpoint);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = i;
      closestPosition = computeDropPosition(orientation, dragEvent, rect);
    }
  });

  // Normalize "After chip N" → "Before chip N+1" so the indicator renders
  // on one consistent edge between adjacent chips.
  if (closestPosition === DropPosition.After && closestIndex < chipElements.length - 1) {
    closestIndex += 1;
    closestPosition = DropPosition.Before;
  }

  return { index: closestIndex, position: closestPosition };
}

/**
 * Computes drop position for a single chip element.
 * Used by per-chip onDragOver handlers in both StepBarChip (horizontal) and StepConfigButton (vertical).
 */
export function getChipDropPosition(
  orientation: DragOrientation,
  dragEvent: React.DragEvent,
  chipElement: Element
): DropPosition {
  return computeDropPosition(orientation, dragEvent, chipElement.getBoundingClientRect());
}
